'use client';

// La fiche acteur telle que l'app l'affiche : maquette v6 « icônes dessinées », validée le
// 02/10/2026. Une seule fiche, deux habillages :
// - `PartnerSheetCard`, l'aperçu en direct du formulaire partenaires (boutons inertes) ;
// - `PartnerSheetHeader` + `PartnerSheetContent`, posés par l'écran de l'app dans sa feuille
//   (offline-place-screen.tsx), avec la vraie carte, les vrais liens et les vrais boutons.
// Les textes viennent tous de sheet-display.ts.

import type { ReactNode } from 'react';

import { Icon, type IconName } from './icons';
import { CONDITION_NAMES } from './referentials';
import {
  acceptedDomains,
  activityProfile,
  callToAction,
  conditionIndexes,
  conditionRangeText,
  frenchDate,
  initials,
  prettyUrl,
  sheetKind,
  sheetLines,
  sheetRules,
  sheetTags,
  typeLine,
  type TagTone,
  type Tone,
} from './sheet-display';
import type { PartnerSheet, RuleSense, SheetLinks } from './types';

const TAG_CLASSES: Record<TagTone, string> = {
  online: 'bg-mist text-deep-sage',
  local: 'bg-reco-teal-soft text-reco-teal',
  money: 'bg-reco-price-soft text-reco-price-ink',
  service: 'bg-sunny-soft text-sunny-ink',
};

const TONE_CLASSES: Record<Tone, string> = {
  green: 'bg-mist text-brand',
  coral: 'bg-reco-price-soft text-coral',
  sunny: 'bg-sunny-soft text-amber-600',
};

// Ce que l'écran de l'app branche sur la fiche. Sans elles (aperçu du formulaire), les
// liens restent ceux de la maquette et les lignes de contact restent du texte.
export type PartnerSheetActions = {
  onOpenUrl: (url: string) => void;
  // Lien ouvert par la ligne du site (celui de l'option, avec son suivi), sinon contact.site.
  siteUrl?: string;
  onCall?: () => void;
  // « Envoyer une demande » n'apparaît que si le canal existe pour cet acteur.
  onEmailRequest?: () => void;
  onReportError?: () => void;
};

export type SheetHours = { label: string; lines: Array<{ jours: string; heures: string }> };

function RuleMark({ sense }: { sense: RuleSense }) {
  if (sense === 'ok') {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-white">
        <Icon name="check" size={12} strokeWidth={3} />
      </span>
    );
  }
  if (sense === 'non') {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-coral text-white">
        <Icon name="x" size={11} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-reco-line bg-white text-xs font-bold text-reco-muted">
      i
    </span>
  );
}

function RoundIcon({ name, className }: { name: IconName; className: string }) {
  return (
    <span className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full ${className}`}>
      <Icon name={name} size={16} />
    </span>
  );
}

function CopyButton({ value, label, onCopied }: { value: string; label: string; onCopied?: (value: string) => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => {
        navigator.clipboard?.writeText(value).then(
          () => onCopied?.(value),
          () => onCopied?.(value),
        );
      }}
      className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl border border-reco-line bg-white text-deep-sage"
    >
      <Icon name="copy" size={16} />
    </button>
  );
}

function InfoRow({ icon, tone, children, end }: { icon: IconName; tone?: string; children: ReactNode; end?: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-b border-reco-line py-2 last:border-b-0">
      <RoundIcon name={icon} className={tone || 'bg-white text-deep-sage'} />
      <div className="min-w-0 flex-1">{children}</div>
      {end}
    </div>
  );
}

// Un lien vers le site de l'acteur : un vrai lien dans l'aperçu, un bouton dans l'app
// (l'app native ouvre les liens externes elle-même).
function OutLink({
  href,
  actions,
  className,
  ariaLabel,
  children,
}: {
  href: string;
  actions?: PartnerSheetActions;
  className: string;
  ariaLabel?: string;
  children: ReactNode;
}) {
  if (actions) {
    return (
      <button type="button" aria-label={ariaLabel} onClick={() => actions.onOpenUrl(href)} className={`text-left ${className}`}>
        {children}
      </button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={ariaLabel} className={className}>
      {children}
    </a>
  );
}

// Une ligne entière cliquable dans l'app (téléphone, demande, site), du texte dans l'aperçu.
function RowAction({ onClick, children }: { onClick?: () => void; children: ReactNode }) {
  if (!onClick) return <>{children}</>;
  return (
    <button type="button" onClick={onClick} className="block w-full text-left">
      {children}
    </button>
  );
}

export function PartnerSheetHeader({
  sheet,
  verified = false,
  distanceLabel,
  end,
}: {
  sheet: PartnerSheet;
  verified?: boolean;
  distanceLabel?: string;
  // Le bouton fermer de la feuille, dans l'app.
  end?: ReactNode;
}) {
  const identity = sheet.identite;
  const local = sheetKind(sheet) === 'local';
  const name = identity.nom;
  const type = typeLine(sheet);
  return (
    <div className="flex items-center gap-3 px-4 pb-2.5 pt-2">
      <div
        className={`flex h-[52px] w-[52px] shrink-0 items-center justify-center overflow-hidden rounded-2xl border text-base font-bold ${
          identity.logo_url ? 'bg-white' : 'bg-deep-sage text-white'
        } ${verified ? 'border-2 border-white ring-[2.5px] ring-coral' : 'border-reco-line'}`}
      >
        {identity.logo_url ? (
          // eslint-disable-next-line @next/next/no-img-element -- logo de l'acteur, hors domaines next/image
          <img src={identity.logo_url} alt="" className="h-full w-full object-contain" />
        ) : (
          initials(name)
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-1.5 text-[19px] font-bold leading-tight">
          {name || 'Votre nom'}
          {verified ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-reco-price-soft py-0.5 pl-1 pr-2 text-[11px] font-bold text-reco-price-ink">
              <span className="text-coral">
                <Icon name="badge" size={15} strokeWidth={2.2} />
              </span>
              Vérifié
            </span>
          ) : null}
        </p>
        <p className="text-sm font-medium leading-snug text-deep-sage">
          {type}
          {local && distanceLabel ? (
            <>
              {type ? ' · ' : ''}
              <b className="whitespace-nowrap font-bold text-coral">{distanceLabel}</b>
            </>
          ) : null}
        </p>
      </div>
      {end}
    </div>
  );
}

export type PartnerSheetContentProps = {
  sheet: PartnerSheet;
  links?: SheetLinks;
  verified?: boolean;
  verifiedOn?: string | null;
  // Qui a confirmé (l'enseigne pour un de ses magasins) ; par défaut, le nom de la fiche.
  confirmedBy?: string;
  sourceDomain?: string;
  consultedOn?: string | null;
  // Mention ajoutée après la source (lieux de l'annuaire ADEME, dont la licence impose sa
  // propre mention pour l'adresse et le téléphone).
  sourceNote?: ReactNode;
  onCopied?: (value: string) => void;
  // Étiquettes du haut, quand l'écran les calcule lui-même (celles de la carte d'option).
  tags?: Array<[string, TagTone]>;
  // La vraie carte, à la place de l'emplacement de l'aperçu.
  map?: ReactNode;
  // Horaires à montrer à la place des horaires de rachat de la fiche (horaires d'ouverture
  // de l'annuaire ADEME, souvent en texte libre).
  hours?: SheetHours;
  actions?: PartnerSheetActions;
  // Domaine et sous-catégorie de l'objet scanné : « Leurs conditions » ne garde que ce qui
  // le concerne.
  objectDomain?: string;
  objectType?: string;
  objectSubtype?: string;
};

export function PartnerSheetContent({
  sheet,
  links = {},
  verified = false,
  verifiedOn,
  confirmedBy,
  sourceDomain,
  consultedOn,
  sourceNote,
  onCopied,
  tags,
  map,
  hours,
  actions,
  objectDomain,
  objectType,
  objectSubtype,
}: PartnerSheetContentProps) {
  const identity = sheet.identite;
  const contact = sheet.contact;
  const local = sheetKind(sheet) === 'local';
  const name = identity.nom;
  const domains = acceptedDomains(sheet);
  const states = conditionIndexes(sheet).sort((a, b) => a - b);
  const rules = sheetRules(sheet, { objectDomain, objectType, objectSubtype });
  const lines = sheetLines(sheet, links);
  const viaLiberio = activityProfile(sheet).rachat && sheet.deroulement.prix_donne.includes('liberio');
  const hoursLabel = hours ? hours.label : 'Horaires pour revendre';
  const hourLines = (hours ? hours.lines : contact.horaires_rachat).filter((line) => line.jours || line.heures);
  // Dans l'app, la ligne « demande » n'existe que si le canal existe pour cet acteur.
  const showsRequest = actions ? Boolean(actions.onEmailRequest) : local || viaLiberio;
  const reportable = !actions || Boolean(actions.onReportError);
  const source = verified
    ? `Infos confirmées par ${confirmedBy || name} le ${frenchDate(verifiedOn, true)}`
    : sourceDomain
      ? `Source : site officiel ${sourceDomain}${consultedOn ? `, consulté le ${frenchDate(consultedOn, false)}` : ''}`
      : null;
  const footer: ReactNode[] = [source, sourceNote].filter(Boolean);
  if (reportable) {
    footer.push(
      actions?.onReportError ? (
        <button type="button" onClick={actions.onReportError} className="font-semibold text-brand">
          Signaler une erreur
        </button>
      ) : (
        <span className="font-semibold text-brand">Signaler une erreur</span>
      ),
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-1.5 px-4 pb-3.5">
        {(tags || sheetTags(sheet)).map(([label, tone]) => (
          <span key={label} className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.08em] ${TAG_CLASSES[tone]}`}>
            {label}
          </span>
        ))}
      </div>

      <div className="flex flex-col gap-3.5 px-4 pb-4">
        {local && map ? map : null}
        {local && !map ? (
          <div className="relative flex h-[150px] items-center justify-center rounded-2xl bg-emerald-50 text-coral shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <Icon name="pin" size={34} />
            <small className="absolute bottom-2 right-3 text-[11px] font-semibold text-reco-muted">Carte</small>
          </div>
        ) : null}

        {identity.presentation ? <p className="whitespace-pre-line leading-relaxed">{identity.presentation}</p> : null}

        {domains.length || states.length ? (
          <section className="flex flex-col gap-3 rounded-2xl bg-logo-cream px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <h4 className="text-base font-semibold text-deep-sage">Ce qu’ils acceptent</h4>
            {domains.length ? (
              <div className="flex flex-wrap gap-1.5">
                {domains.map((domain) => (
                  <span key={domain.code} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs font-semibold">
                    <span className="text-brand">
                      <Icon name={domain.icon} size={14} strokeWidth={2.2} />
                    </span>
                    {domain.name}
                  </span>
                ))}
              </div>
            ) : null}
            {states.length ? (
              <div>
                <p className="mt-1 text-xs font-semibold text-reco-muted">
                  États acceptés : <span className="text-deep-sage">{conditionRangeText(sheet)}</span>
                </p>
                <div className="relative h-[22px]">
                  <span className="absolute left-[10%] right-[10%] top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-reco-line" />
                  <span
                    className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-brand"
                    style={{ left: `${10 + 20 * states[0]}%`, width: `${20 * (states[states.length - 1] - states[0])}%` }}
                  />
                  {CONDITION_NAMES.map((label, index) => (
                    <span
                      key={label}
                      className={`absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full ${
                        states.includes(index) ? 'border-[3px] border-white bg-brand ring-[1.5px] ring-brand' : 'border-2 border-slate-300 bg-white'
                      }`}
                      style={{ left: `${10 + 20 * index}%` }}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-5 text-center text-[11px] font-medium leading-tight text-slate-400">
                  {CONDITION_NAMES.map((label, index) => (
                    <span key={label} className={states.includes(index) ? 'font-bold text-deep-sage' : ''}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {rules.length ? (
          <section className="flex flex-col gap-2.5 rounded-2xl bg-logo-cream px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <h4 className="text-base font-semibold text-deep-sage">Leurs conditions</h4>
            {rules.map((rule, index) => (
              <div key={`${rule.text}-${index}`} className="flex gap-2.5 leading-snug">
                <RuleMark sense={rule.sense} />
                <span>{rule.text}</span>
              </div>
            ))}
            {links.conditions ? (
              <OutLink href={links.conditions} actions={actions} className="inline-flex items-center gap-1 text-sm font-semibold text-deep-sage">
                Toutes leurs conditions <Icon name="arrow" size={14} strokeWidth={2.2} />
              </OutLink>
            ) : null}
          </section>
        ) : null}

        {lines.length ? (
          <section className="flex flex-col rounded-2xl bg-logo-cream px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <h4 className="mb-1.5 text-base font-semibold text-deep-sage">Comment ça se passe</h4>
            {lines.map((line) => (
              <div key={`${line.key}-${line.label}`} className="flex items-start gap-3 border-b border-reco-line py-2 last:border-b-0">
                <RoundIcon name={line.icon} className={TONE_CLASSES[line.tone]} />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold leading-tight text-reco-muted">{line.label}</span>
                  <span className="block leading-snug">{line.value}</span>
                </span>
                {line.href ? (
                  <OutLink
                    href={line.href}
                    actions={actions}
                    ariaLabel="Voir sur leur site"
                    className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center text-deep-sage"
                  >
                    <Icon name="arrow" size={15} strokeWidth={2.2} />
                  </OutLink>
                ) : null}
              </div>
            ))}
          </section>
        ) : null}

        <section className="flex flex-col rounded-2xl bg-logo-cream px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
          <h4 className="mb-1.5 text-base font-semibold text-deep-sage">Infos pratiques</h4>
          {local && contact.adresse ? (
            <InfoRow icon="pin" end={<CopyButton value={contact.adresse} label="Copier l’adresse" onCopied={onCopied} />}>
              <span className="leading-snug">{contact.adresse}</span>
            </InfoRow>
          ) : null}
          {contact.telephone ? (
            <InfoRow icon="phone" end={<CopyButton value={contact.telephone} label="Copier le numéro" onCopied={onCopied} />}>
              <RowAction onClick={actions?.onCall}>
                <span className="font-semibold text-deep-sage">{contact.telephone}</span>
              </RowAction>
            </InfoRow>
          ) : null}
          {contact.email && contact.email_affiche === 'oui' ? (
            <InfoRow icon="mail" end={<CopyButton value={contact.email} label="Copier l’e-mail" onCopied={onCopied} />}>
              <span className="break-all font-semibold text-deep-sage">{contact.email}</span>
            </InfoRow>
          ) : showsRequest ? (
            <InfoRow icon="mail">
              <RowAction onClick={actions?.onEmailRequest}>
                <span className="block font-semibold text-deep-sage">{viaLiberio ? 'Demander une estimation →' : 'Envoyer une demande →'}</span>
                <span className="block text-xs text-reco-muted">Par e-mail, via Liberio</span>
              </RowAction>
            </InfoRow>
          ) : null}
          {local && hourLines.length ? (
            <div className="flex items-start gap-3 border-b border-reco-line py-2 last:border-b-0">
              <RoundIcon name="clock" className="bg-sunny-soft text-amber-600" />
              <div className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-reco-muted">{hoursLabel}</span>
                <span className="grid grid-cols-[70px_1fr] gap-y-0.5 leading-snug">
                  {hourLines.map((line, index) => (
                    <span key={index} className="contents">
                      {line.jours ? <span>{line.jours}</span> : null}
                      {/* Horaires en texte libre (annuaire ADEME) : sur toute la largeur. */}
                      <span className={line.jours ? '' : 'col-span-2 whitespace-pre-line'}>{line.heures}</span>
                    </span>
                  ))}
                </span>
              </div>
            </div>
          ) : null}
          {contact.aide_url ? (
            <InfoRow icon="chat" end={<span className="text-deep-sage"><Icon name="arrow" size={16} /></span>}>
              <RowAction onClick={actions ? () => actions.onOpenUrl(contact.aide_url) : undefined}>
                <span className="block font-semibold text-deep-sage">Discussion en ligne</span>
                {contact.aide_texte ? <span className="block text-xs text-reco-muted">{contact.aide_texte}</span> : null}
              </RowAction>
            </InfoRow>
          ) : null}
          {contact.site ? (
            <InfoRow icon="globe" end={<span className="text-deep-sage"><Icon name="arrow" size={16} /></span>}>
              <RowAction onClick={actions ? () => actions.onOpenUrl(actions.siteUrl || contact.site) : undefined}>
                <span className="font-semibold text-deep-sage">{local ? 'Page du magasin' : prettyUrl(contact.site)}</span>
              </RowAction>
            </InfoRow>
          ) : null}
        </section>

        {footer.length ? (
          <p className="mt-0.5 text-center text-[11px] leading-snug text-reco-muted">
            {footer.map((part, index) => (
              <span key={index}>
                {index ? ' · ' : ''}
                {part}
              </span>
            ))}
          </p>
        ) : null}
      </div>
    </>
  );
}

export type PartnerSheetCardProps = {
  sheet: PartnerSheet;
  links?: SheetLinks;
  verified?: boolean;
  verifiedOn?: string | null;
  sourceDomain?: string;
  consultedOn?: string | null;
  distanceLabel?: string;
  onCopied?: (value: string) => void;
};

// L'aperçu du formulaire partenaires : la fiche dans un faux téléphone, boutons inertes.
export default function PartnerSheetCard({
  sheet,
  links = {},
  verified = false,
  verifiedOn,
  sourceDomain,
  consultedOn,
  distanceLabel,
  onCopied,
}: PartnerSheetCardProps) {
  const local = sheetKind(sheet) === 'local';

  return (
    <div className="flex flex-col bg-white text-sm text-ink">
      <div className="flex justify-center pb-1 pt-2.5">
        <span className="h-1 w-9 rounded-full bg-reco-line" />
      </div>

      <PartnerSheetHeader sheet={sheet} verified={verified} distanceLabel={distanceLabel} />

      <PartnerSheetContent
        sheet={sheet}
        links={links}
        verified={verified}
        verifiedOn={verifiedOn}
        sourceDomain={sourceDomain}
        consultedOn={consultedOn}
        onCopied={onCopied}
      />

      <div className="flex flex-col gap-2 border-t border-slate-100 px-4 pb-5 pt-3">
        <span className="rounded-xl bg-brand py-3 text-center text-base font-semibold text-white">{callToAction(sheet)}</span>
        {local ? (
          <div className="grid grid-cols-2 gap-2">
            <span className="rounded-xl border border-brand-border py-3 text-center font-semibold text-deep-sage">Itinéraire →</span>
            <span className="rounded-xl border border-brand-border py-3 text-center font-semibold text-deep-sage">Appeler</span>
          </div>
        ) : (
          <span className="rounded-xl border border-brand-border py-3 text-center font-semibold text-deep-sage">Ouvrir le site</span>
        )}
      </div>
    </div>
  );
}
